import { defineStore } from "pinia";
import router from "@/router";
import notifications from "@/resources/notifications";
import { eraseCookie, getCookie } from "@/utils";
import { userService } from "@/services";
import type { ISimpleUser, IUserResponse } from "@/types/response";
import type { IBaseForm, IPasswordChangeDate } from "@/types/general";

interface State {
  user?: IUserResponse;
  users: ISimpleUser[];
  token: string | null;
  passwordChangeData: IPasswordChangeDate;
  baseLoading?: boolean;
}

export const useUserStore = defineStore("user", {
  state: () =>
    ({
      user: {} as IUserResponse,
      token: getCookie("jwtToken") ?? null,
      passwordChangeData: {} as IPasswordChangeDate,
      users: [] as ISimpleUser[],
      userCreateFrom: {} as IBaseForm,
      baseLoading: false,
    }) as State,
  getters: {
    isAdminUser: (state) => state.user?.userRoles?.includes("Admin"),
    userInfo: (state) => state.user,
    isLoggedIn: (state) => !!state.token,
  },
  actions: {
    async login({
      email,
      password,
      siteToken,
    }: {
      email: string;
      password: string;
      siteToken: string;
    }) {
      return await userService.login(email, password, siteToken);
    },
    setToken(token: string | null) {
      this.token = token;
    },
    async logout(path = "") {
      this.setToken("");
      eraseCookie("jwtToken");
      this.user = {} as IUserResponse;
      await router.push({
        name: "Login",
        query: { redirect: path || router.currentRoute.value.fullPath },
      });
    },
    async getUser() {
      if (this.user?.userName || !this.isLoggedIn) {
        return;
      }
      this.baseLoading = true;
      this.user = await userService.getUser();
      setTimeout(() => {
        this.baseLoading = false;
      }, 500);
    },
    async getUpdatedUser() {
      this.user = await userService.getUser();
    },
    isPasswordMatched(newPass: string, newPassConfirm: string) {
      const isNotMatchedCondition = newPass !== newPassConfirm;
      if (isNotMatchedCondition) {
        notifications.warning.passwordNotMatched();
        return false;
      }
      return true;
    },
  },
});
